import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { captureOnboardingCompleted } from '../utils/authProductEvents';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useRealm } from '../context/RealmContext';
import { useAuth } from '../context/AuthContext';
import { SpaceProvider } from '../context/SpaceContext';
import RealmTransition from './RealmTransition';
import OuterLayout from './layouts/OuterLayout';
import HomePage from '../pages/HomePage';
import DashboardPage from '../pages/DashboardPage';
import SpacesPage from '../pages/SpacesPage';
import MemoryMapRouteGate from './MemoryMapRouteGate';
import { isPostHogConfigured } from '../utils/posthogEnv';
import { isMemoryMapLocalOverrideEnabled } from '../utils/featureFlags';
import DocsPage from '../pages/DocsPage';
import ChromeExtensionDocsPage from '../pages/ChromeExtensionDocsPage';
import XBookmarksDocsPage from '../pages/XBookmarksDocsPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import TermsOfServicePage from '../pages/TermsOfServicePage';
import SupportPage from '../pages/SupportPage';
import PricingPage from '../pages/PricingPage';
import IntegrationsDirectoryPage from '../pages/IntegrationsDirectoryPage';
import BlogPage from '../pages/BlogPage';
import BlogPostPage from '../pages/BlogPostPage';
import CareersPage from '../pages/CareersPage';
import MarketingLayout from './layouts/MarketingLayout';

import BillingSuccessPage from '../pages/BillingSuccessPage';
import BillingCancelPage from '../pages/BillingCancelPage';
import BillingUpgradePage from '../pages/BillingUpgradePage';
import SettingsPage from '../pages/SettingsPage';
import ChatGPTCustomActionsLanding from '../pages/integrations/ChatGPTCustomActionsLanding';
import ClaudeDesktopLanding from '../pages/integrations/ClaudeDesktopLanding';
import ZapierLanding from '../pages/integrations/ZapierLanding';
import MakeLanding from '../pages/integrations/MakeLanding';
import SlackLanding from '../pages/integrations/SlackLanding';
import NotionLanding from '../pages/integrations/NotionLanding';
import GitHubLanding from '../pages/integrations/GitHubLanding';
import WhatsAppBasicLanding from '../pages/integrations/WhatsAppBasicLanding';
import DiscordLanding from '../pages/integrations/DiscordLanding';
import TelegramLanding from '../pages/integrations/TelegramLanding';
import WhatsAppBusinessLanding from '../pages/integrations/WhatsAppBusinessLanding';
import N8NLanding from '../pages/integrations/N8NLanding';
import DifyLanding from '../pages/integrations/DifyLanding';
import IFTTTLanding from '../pages/integrations/IFTTTLanding';
import MSTeamsLanding from '../pages/integrations/MSTeamsLanding';
import GoogleWorkspaceLanding from '../pages/integrations/GoogleWorkspaceLanding';
import BareAPILanding from '../pages/integrations/BareAPILanding';
import BareMCPLanding from '../pages/integrations/BareMCPLanding';
import CursorLanding from '../pages/integrations/CursorLanding';
import WizardLanding from '../pages/WizardLanding';
import XBookmarksLanding from '../pages/integrations/XBookmarksLanding';
import MemaraVsMem0 from '../pages/comparisons/MemaraVsMem0';
import VectorDatabaseVsTraditionalDatabase from '../pages/comparisons/VectorDatabaseVsTraditionalDatabase';
import ImageMemoriesPage from '../pages/roadmap/ImageMemoriesPage';
import AudioMemoriesPage from '../pages/roadmap/AudioMemoriesPage';
import VideoMemoriesPage from '../pages/roadmap/VideoMemoriesPage';
import RoadmapNavigation from './roadmap/RoadmapNavigation';
import LoginSignup from './auth/LoginSignup';
import AuthCallback from './auth/AuthCallback';
import AuthenticatePage from '../pages/AuthenticatePage';
import ImprovedOnboardingFlow from './onboarding/ImprovedOnboardingFlow';
import NotFoundPage from '../pages/NotFoundPage';
import UnsubscribePage from '../pages/UnsubscribePage';
import { apiService } from '../services/api';
import { spacesCache } from '../hooks/useSpacesQuery';
import { authDebugLog } from '../utils/authDebugLog';
import { isKnownAppRoute } from '../lib/appRoutes';
import {
  readRememberedWorkspaceSection,
  workspaceSectionPath,
} from '../lib/workspaceSection';

const MemoryMapPage = lazy(() => import('../pages/MemoryMapPage'));

export default function RealmRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentRealm } = useRealm();
  const { isAuthenticated, isLoading, user } = useAuth();
  const posthog = usePostHog();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCheckingOnboardingStatus, setIsCheckingOnboardingStatus] = useState(false);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
  
  // Ref guard to prevent multiple redirects
  const hasRedirectedRef = useRef(false);

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (!isLoading && isAuthenticated && (location.pathname === '/login' || location.pathname === '/signup')) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  // Check if user needs onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Safety checks - don't proceed if conditions aren't met
      if (isLoading || !isAuthenticated || !user || hasCheckedOnboarding) {
        return;
      }

      // Ensure we have a valid user ID
      const userId = user?.id;
      if (!userId) {
        setHasCheckedOnboarding(true);
        return;
      }

      setIsCheckingOnboardingStatus(true);

      try {
        // Check 1: localStorage flag (fast check)
        const hasSeenOnboardingFlag = localStorage.getItem(`onboarding_completed_${userId}`);
        
        if (hasSeenOnboardingFlag) {
          setShowOnboarding(false);
          setHasCheckedOnboarding(true);
          return;
        }

        // Check 2: Backend API check - if user has any spaces, they're not new
        try {
          const spacesResponse = await apiService.getAllSpaces();
          const userHasSpaces = spacesResponse?.spaces && spacesResponse.spaces.length > 0;
          
          if (userHasSpaces) {
            // User has spaces, they're not new - set localStorage flag and skip onboarding
            localStorage.setItem(`onboarding_completed_${userId}`, 'true');
            setShowOnboarding(false);
          } else {
            // User has no spaces, they need onboarding
            setShowOnboarding(true);
          }
        } catch (apiError) {
          console.warn('⚠️ Failed to check user spaces:', apiError);
          // API failed, fall back to showing onboarding for safety
          // This ensures new users don't miss onboarding due to API issues
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error('❌ Error checking onboarding status:', error);
        // On error, default to showing onboarding for new users
        setShowOnboarding(true);
      } finally {
        setIsCheckingOnboardingStatus(false);
        setHasCheckedOnboarding(true);
      }
    };

    // Only run the check when we have all required data and haven't checked yet
    if (isAuthenticated && user && !isLoading && !hasCheckedOnboarding) {
      checkOnboardingStatus().catch(error => {
        console.error('❌ Unhandled error in onboarding check:', error);
        setIsCheckingOnboardingStatus(false);
        setHasCheckedOnboarding(true);
        // Default to not showing onboarding if there's an unhandled error
        setShowOnboarding(false);
      });
    }
  }, [isAuthenticated, user, isLoading, hasCheckedOnboarding]);

  const handleOnboardingComplete = () => {
    captureOnboardingCompleted(posthog, user?.id, undefined, user?.email);
    // Drop any list cached before the space created during onboarding.
    spacesCache.invalidate();
    setShowOnboarding(false);
    setHasCheckedOnboarding(true);
  };

  // Handle navigation redirects in useEffect to avoid setState during render
  useEffect(() => {
    if (isAuthenticated && !showOnboarding && !isLoading && !isCheckingOnboardingStatus) {
      const pathname = location.pathname;
      if ((pathname === '/' || pathname === '/home') && !hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        try {
          sessionStorage.setItem('memara_show_dashboard_hint', 'true');
        } catch (_) {}
        navigate('/dashboard', { replace: true });
      } else if (pathname === '/integrations') {
        navigate('/dashboard?view=integrations', { replace: true });
      }
    }

    // Reset redirect flag when auth state changes (user logs out)
    if (!isAuthenticated) {
      hasRedirectedRef.current = false;
    }
  }, [isAuthenticated, showOnboarding, isLoading, isCheckingOnboardingStatus, location.pathname, navigate]);

  // Onboarding status starts unchecked. Treat that gap as loading so
  // SpaceProvider cannot mount and cache an empty list before the check.
  const onboardingCheckPending = Boolean(
    isAuthenticated && user && !hasCheckedOnboarding
  );

  // Show loading while authentication is checking status or onboarding status
  if (isLoading || isCheckingOnboardingStatus || onboardingCheckPending) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={isLoading ? 'Loading...' : 'Checking account status...'}
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" aria-hidden />
          <span className="text-lg text-gray-600">
            {isLoading ? 'Loading...' : 'Checking account status...'}
          </span>
        </div>
      </div>
    );
  }

  // Handle documentation pages - accessible without authentication
  if (location.pathname === '/docs') {
    return <DocsPage />;
  }

  if (location.pathname === '/docs/integrations/chrome-extension') {
    return <ChromeExtensionDocsPage />;
  }

  if (location.pathname === '/docs/integrations/x-bookmarks') {
    return <XBookmarksDocsPage />;
  }

  // Published n8n nodes still link here. These paths are not docs pages.
  const legacyDocPath = location.pathname.replace(/\/$/, '') || '/';
  if (legacyDocPath === '/docs/integrations/n8n' || legacyDocPath === '/docs/api-keys') {
    return <Navigate to="/integrations/n8n" replace />;
  }

  if (location.pathname === '/pricing') {
    return <PricingPage />;
  }

  // Handle support page - accessible without authentication
  if (location.pathname === '/support') {
    return <SupportPage />;
  }

  if (location.pathname === '/join-us') {
    return <Navigate to="/careers" replace />;
  }

  if (location.pathname === '/careers') {
    return (
      <MarketingLayout>
        <CareersPage />
      </MarketingLayout>
    );
  }

  // Handle legal pages - accessible without authentication
  if (location.pathname === '/legal/privacy') {
    return <Navigate to="/legal/privacy-policy" replace />;
  }

  if (location.pathname === '/legal/privacy-policy') {
    return <PrivacyPolicyPage />;
  }

  if (location.pathname === '/legal/terms' || location.pathname === '/terms') {
    return <TermsOfServicePage />;
  }

  if (location.pathname === '/unsubscribe') {
    return <UnsubscribePage />;
  }

  // Handle billing pages - these should be accessible without full authentication
  if (location.pathname === '/billing/success') {
    return <BillingSuccessPage />;
  }
  
  if (location.pathname === '/billing/cancel') {
    return <BillingCancelPage />;
  }

  if (location.pathname === '/billing/upgrade') {
    return <BillingUpgradePage />;
  }

  // Settings page - requires authentication
  if (location.pathname === '/settings' || location.pathname.startsWith('/settings/')) {
    return <SettingsPage />;
  }

  // Handle integration landing pages - accessible without authentication
  if (location.pathname === '/integrations/chatgpt') {
    return <ChatGPTCustomActionsLanding />;
  }
  
  if (location.pathname === '/integrations/chatgpt-custom-actions') {
    return <ChatGPTCustomActionsLanding />;
  }
  
  if (location.pathname === '/integrations/claude-desktop') {
    return <ClaudeDesktopLanding />;
  }

  if (location.pathname === '/integrations/zapier') {
    return <ZapierLanding />;
  }

  if (location.pathname === '/integrations/make') {
    return <MakeLanding />;
  }

  if (location.pathname === '/integrations/slack') {
    return <SlackLanding />;
  }

  if (location.pathname === '/integrations/notion') {
    return <NotionLanding />;
  }

  if (location.pathname === '/integrations/github') {
    return <GitHubLanding />;
  }

  if (location.pathname === '/integrations/whatsapp') {
    return <WhatsAppBasicLanding />;
  }
  
  if (location.pathname === '/integrations/whatsapp-basic') {
    return <WhatsAppBasicLanding />;
  }

  if (location.pathname === '/integrations/discord') {
    return <DiscordLanding />;
  }

  if (location.pathname === '/integrations/telegram') {
    return <TelegramLanding />;
  }

  if (location.pathname === '/integrations/whatsapp-business') {
    return <WhatsAppBusinessLanding />;
  }

  if (location.pathname === '/integrations/n8n') {
    return <N8NLanding />;
  }

  if (location.pathname === '/integrations/dify') {
    return <DifyLanding />;
  }

  if (location.pathname === '/integrations/ifttt') {
    return <IFTTTLanding />;
  }

  if (location.pathname === '/integrations/microsoft-teams') {
    return <MSTeamsLanding />;
  }

  if (location.pathname === '/integrations/google-workspace') {
    return <GoogleWorkspaceLanding />;
  }

  if (location.pathname === '/integrations/bare-api') {
    return <BareAPILanding />;
  }

  if (location.pathname === '/integrations/bare-mcp') {
    return <BareMCPLanding />;
  }

  if (location.pathname === '/wizard') {
    return <WizardLanding />;
  }

  if (location.pathname === '/integrations/chrome-extension') {
    return <Navigate to="/docs/integrations/chrome-extension" replace />;
  }

  if (location.pathname === '/integrations/cursor') {
    return <CursorLanding />;
  }

  if (location.pathname === '/integrations/twitter-bookmarks') {
    return <Navigate to="/integrations/x-bookmarks" replace />;
  }

  if (location.pathname === '/integrations/x-bookmarks') {
    return <XBookmarksLanding />;
  }

  if (location.pathname === '/integrations/directory') {
    return <Navigate to="/integrations" replace />;
  }

  if (location.pathname === '/integrations' && !isAuthenticated) {
    return <IntegrationsDirectoryPage />;
  }

  // Comparison/alternatives pages - accessible without authentication
  if (location.pathname === '/comparisons/memara-vs-mem0') {
    return <MemaraVsMem0 />;
  }
  if (location.pathname === '/alternatives/mem0') {
    return <MemaraVsMem0 />;
  }
  if (location.pathname === '/comparisons/vector-database-vs-traditional-database') {
    return <VectorDatabaseVsTraditionalDatabase />;
  }

  // Handle roadmap pages - accessible without authentication
  if (location.pathname === '/roadmap') {
    return <RoadmapNavigation />;
  }
  
  if (location.pathname === '/roadmap/image-memories') {
    return <ImageMemoriesPage />;
  }
  
  if (location.pathname === '/roadmap/audio-memories') {
    return <AudioMemoriesPage />;
  }
  
  if (location.pathname === '/roadmap/video-memories') {
    return <VideoMemoriesPage />;
  }

  // Handle blog routes - accessible without authentication
  if (location.pathname === '/blog') {
    return (
      <MarketingLayout showSkipLink={false}>
        <BlogPage />
      </MarketingLayout>
    );
  }

  if (location.pathname.startsWith('/blog/')) {
    // Extract the slug from the pathname
    const slug = location.pathname.replace('/blog/', '');
    
    return (
      <MarketingLayout showSkipLink={false}>
        <BlogPostPage blogSlug={slug} />
      </MarketingLayout>
    );
  }

  // Handle login/signup routes explicitly
  if (location.pathname === '/login' || location.pathname === '/signup') {
    return <LoginSignup />;
  }

  // Show landing page for unauthenticated users at root
  if (!isAuthenticated && (location.pathname === '/' || location.pathname === '/home')) {
    // Use a basic layout without user authentication UI
    return (
      <MarketingLayout>
        <HomePage />
      </MarketingLayout>
    );
  }

  // Handle auth callback route (special case - no authentication required)
  if (location.pathname === '/auth/callback') {
    console.log('RealmRouter: Rendering AuthCallback for path:', location.pathname);
    console.log('RealmRouter: Search params:', location.search);
    return <AuthCallback />;
  }

  // Handle authenticate route (special case - handles OAuth flows)
  if (location.pathname === '/authenticate') {
    console.log('RealmRouter: Rendering AuthenticatePage for path:', location.pathname);
    console.log('RealmRouter: Search params:', location.search);
    return <AuthenticatePage />;
  }
  
  // Define known protected routes
  const isProtectedRoute = location.pathname.startsWith('/dashboard') ||
                          location.pathname.startsWith('/analytics') ||
                          location.pathname.startsWith('/spaces') ||
                          location.pathname === '/workspace';
  
  // Redirect unauthenticated users trying to access protected routes
  if (!isAuthenticated && isProtectedRoute) {
    authDebugLog('RealmRouter: unauthenticated protected route → LoginSignup', {
      pathname: location.pathname,
      isLoading,
      isCheckingOnboardingStatus,
      isProtectedRoute,
    });
    return <LoginSignup />;
  }

  // Unknown paths render the not-found page for every auth state.
  // Prefix matches such as /integrations/* used to fall through to login.
  if (!isKnownAppRoute(location.pathname)) {
    authDebugLog('RealmRouter: unknown route → NotFoundPage', {
      pathname: location.pathname,
      isLoading,
      isCheckingOnboardingStatus,
      isAuthenticated,
    });
    return <NotFoundPage />;
  }

  // Redirect unauthenticated users to login (fallback)
  if (!isAuthenticated) {
    authDebugLog('RealmRouter: unauthenticated fallback → LoginSignup', {
      pathname: location.pathname,
      isLoading,
      isCheckingOnboardingStatus,
    });
    return <LoginSignup />;
  }

  // Show onboarding for new users
  if (showOnboarding) {
    return <ImprovedOnboardingFlow onComplete={handleOnboardingComplete} />;
  }

  // Authenticated user experience with dual realms
  const renderContent = () => {
    try {
      // Extract pathname from current location
      const pathname = location.pathname;
      
      // Memory Map (must be before broad `/spaces` match); gated by PostHog `memory_map` or local/dev override
      if (pathname === '/spaces/map' || pathname === '/workspace/map') {
        const listPath = pathname.startsWith('/workspace') ? '/workspace' : '/spaces/memories';
        const mapFallback = (
          <div
            className="flex min-h-screen items-center justify-center bg-workspace-surface-50 text-workspace-text-secondary"
            role="status"
            aria-live="polite"
          >
            Loading…
          </div>
        );

        if (!isPostHogConfigured()) {
          if (!isMemoryMapLocalOverrideEnabled()) {
            return <Navigate to={listPath} replace />;
          }
          return (
            <SpaceProvider>
              <Suspense fallback={mapFallback}>
                <MemoryMapPage />
              </Suspense>
            </SpaceProvider>
          );
        }

        return (
          <SpaceProvider>
            <MemoryMapRouteGate />
          </SpaceProvider>
        );
      }

      if (pathname === '/spaces') {
        const lastSection = readRememberedWorkspaceSection();
        return <Navigate to={workspaceSectionPath(lastSection, 'spaces')} replace />;
      }

      if (pathname.startsWith('/spaces') || pathname === '/workspace') {
        return (
          <SpaceProvider>
            <SpacesPage />
          </SpaceProvider>
        );
      }
      
      if (pathname.startsWith('/dashboard') || pathname.startsWith('/analytics')) {
        return (
          <SpaceProvider>
            <OuterLayout>
              <DashboardPage />
            </OuterLayout>
          </SpaceProvider>
        );
      }
      
      // Don't render anything if we're about to redirect - useEffect will handle this
      if (pathname === '/' || pathname === '/home' || pathname === '/integrations') {
        return null;
      }
      
      // Fallback: Check realm state for backward compatibility
      if (currentRealm === 'workspace') {
        return (
          <SpaceProvider>
            <SpacesPage />
          </SpaceProvider>
        );
      }

      // Check if this might be a 404 for authenticated users
      // If the path doesn't match any known routes, show 404
      if (!pathname.startsWith('/dashboard') && 
          !pathname.startsWith('/spaces') && 
          !pathname.startsWith('/workspace') &&
          pathname !== '/' && 
          pathname !== '/home' &&
          pathname !== '/integrations') {
        return <NotFoundPage />;
      }

      // Default to dashboard for authenticated users
      return (
        <SpaceProvider>
          <OuterLayout>
            <DashboardPage />
          </OuterLayout>
        </SpaceProvider>
      );
    } catch (error) {
      console.error('❌ Error rendering content:', error);
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
                  <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Display Error</h1>
          <p className="text-gray-600 mb-4">There was a problem loading this page. Please refresh or try again.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
  };

  try {
    return (
      <RealmTransition>
        {renderContent()}
      </RealmTransition>
    );
  } catch (error) {
    console.error('❌ Error in RealmRouter render:', error);
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Application Error</h1>
          <p className="text-gray-600 mb-4">An unexpected error occurred. Please refresh the page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
} 